"use client"

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from "recharts"
import { type ChartDimension } from "./types"
import type { CSSProperties } from "react"

/** A row in a population pyramid — a category label plus two opposing
 *  numeric values (typically male/female counts). */
export interface PyramidRow {
  name: string
  left: number
  right: number
}

export interface PopulationPyramidProps {
  data: PyramidRow[]
  leftLabel?: string
  rightLabel?: string
  leftColor?: string
  rightColor?: string
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  showValueLabels?: boolean
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
}

export function PopulationPyramid({
  data, leftLabel = "Left", rightLabel = "Right",
  leftColor = "#3b82f6", rightColor = "#ec4899",
  showLegend = true, showGrid = true, showTooltip = true, showValueLabels,
  className, style, height = "100%", width = "100%",
}: PopulationPyramidProps) {
  // Mirror the left axis — left bars are negative so they extend leftward,
  // right bars stay positive. Tooltip needs the absolute values for a sane
  // hover, so we keep both originals on the row.
  const rows = data.map(r => ({
    name: r.name,
    [leftLabel]: -Math.abs(r.left),
    [rightLabel]: Math.abs(r.right),
    _absLeft: Math.abs(r.left),
    _absRight: Math.abs(r.right),
  }))

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <BarChart data={rows} layout="vertical" stackOffset="sign" margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        <XAxis type="number" tick={{ fontSize: 10.5 }} tickFormatter={(v) => Math.abs(Number(v)).toString()} />
        <YAxis dataKey="name" type="category" tick={{ fontSize: 10.5 }} width={70} />
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }}
          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }}
          formatter={(v) => Math.abs(Number(v))}
        />}
        {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar dataKey={leftLabel} fill={leftColor} stackId="p" isAnimationActive={false} radius={[0, 3, 3, 0]}>
          {showValueLabels && <LabelList dataKey="_absLeft" position="left" style={{ fontSize: 10 }} />}
        </Bar>
        <Bar dataKey={rightLabel} fill={rightColor} stackId="p" isAnimationActive={false} radius={[3, 0, 0, 3]}>
          {showValueLabels && <LabelList dataKey="_absRight" position="right" style={{ fontSize: 10 }} />}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
